/**
 * Confirmation Dialog Component
 * Reusable confirmation dialog for delete, remove, or other destructive actions
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  spacing,
  typographyColors,
  typography,
  fontPrimary,
} from '@/design-system/utils';

export interface ConfirmationDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * Callback when the dialog open state changes
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Dialog title
   */
  title: string;
  /**
   * Dialog description/message
   */
  description: string;
  /**
   * Label for the confirm button
   * @default "Confirm"
   */
  confirmLabel?: string;
  /**
   * Label for the cancel button
   * @default "Cancel"
   */
  cancelLabel?: string;
  /**
   * Callback when confirm button is clicked
   */
  onConfirm: () => void;
  /**
   * Optional callback when cancel button is clicked
   * If not provided, uses onOpenChange(false)
   */
  onCancel?: () => void;
  /**
   * Whether to show the close button
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Variant for the confirm button
   * @default "default"
   */
  confirmVariant?: 'default' | 'destructive';
}

/**
 * Reusable Confirmation Dialog Component
 *
 * @example
 * ```tsx
 * <ConfirmationDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Delete Chat"
 *   description="Are you sure you want to delete this chat? This action cannot be undone."
 *   confirmLabel="Delete"
 *   onConfirm={handleDelete}
 * />
 * ```
 */
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  showCloseButton = true,
  confirmVariant = 'default',
}) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange(false);
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const confirmButtonStyle: React.CSSProperties = {
    padding: `${spacing[2]} ${spacing[4]}`,
    borderRadius: '6px',
    border: 'none',
    background: confirmVariant === 'destructive' ? '#DC2626' : '#000',
    color: '#fff',
    fontFamily: fontPrimary,
    fontSize: typography.size.base,
    fontWeight: typography.weight.regular,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={showCloseButton}>
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.lg,
              fontWeight: typography.weight.semibold,
              color: typographyColors.text,
            }}
          >
            {title}
          </DialogTitle>
          <DialogDescription
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.base,
              color: typographyColors.secondary,
              marginTop: spacing[2],
            }}
          >
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter
          style={{
            display: 'flex',
            gap: spacing[3],
            marginTop: spacing[4],
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={handleCancel}
            style={{
              padding: `${spacing[2]} ${spacing[4]}`,
              borderRadius: '6px',
              border: '1px solid #E7E5E4',
              background: 'transparent',
              color: typographyColors.text,
              fontFamily: fontPrimary,
              fontSize: typography.size.base,
              fontWeight: typography.weight.regular,
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#F5F5F4';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            style={confirmButtonStyle}
            onMouseEnter={e => {
              e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            {confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
